import React, { forwardRef, useRef } from 'react';
import _merge from 'lodash/merge';
import { useSpring, animated } from 'react-spring';
import Elevation, { ElevationProps } from 'components/Elevation';
import { getContext, getEasing, getDuration, useCombinedRefs, isAnimationDisabled } from 'common';

import { SPEED_SLOW } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { TRANSITION_STANDARD } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';

import styled from './styles';

export const STATUS = {
  default: 'default',
  cleared: 'cleared',
  warning: 'warning'
};

export const StatusIndicatorContext = React.createContext<any>({});

export interface StatusIndicatorProps extends React.ComponentPropsWithRef<'div'> {
  /** The content of the StatusIndicator, consider using StatusIndicatorWarningOverlay. */
  children: React.ReactNode;
  /** The status type. */
  status?: 'default' | 'cleared' | 'warning';
  /** Control animation config here. This uses react-spring transitions config. See https://react-spring.io for more information. */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Override the Elevation properties. See Elevation component for details. */
  elevationProps?: Partial<ElevationProps>;
}
/**
 * StatusIndicator component.
 * Any other props than the explicitly defined props will be provided to the StatusIndicator wrapper.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference).
 */
const StatusIndicator = forwardRef<HTMLElement, StatusIndicatorProps>((props, ref) =>
  getContext(({ RTL, theme, disableAnimation }) => {
    const {
      children,
      status,
      animationProps: passedAnimationProps,
      disableAnimation: propsdisableAnimation,
      theme: propTheme,
      elevationProps,
      ...otherProps
    } = props;

    const containerRef = useRef();
    const combinedRef = useCombinedRefs(ref, containerRef);

    const resolvedTheme = _merge({}, theme.STATUS_INDICATOR, propTheme);

    const animationProps = useSpring(passedAnimationProps || {
      height: status === STATUS.default ? '0px' : '40px',
      config: {
        duration: getDuration(SPEED_SLOW),
        easing: getEasing(TRANSITION_STANDARD)
      },
      immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
    });

    return (
      <Elevation {...elevationProps}>
        <styled.StatusIndicator
          ref={combinedRef}
          as={animated.div}
          status={status}
          $theme={resolvedTheme}
          $rtl={RTL}
          style={animationProps}
          {...otherProps}
        >
          <StatusIndicatorContext.Provider
            value={{
              $theme: resolvedTheme,
              status,
              containerRef: combinedRef
            }}
          >
            {children}
          </StatusIndicatorContext.Provider>
        </styled.StatusIndicator>
      </Elevation>
    );
  }));
StatusIndicator.displayName = 'StatusIndicator';

StatusIndicator.defaultProps = {
  elevationProps: {},
  animationProps: undefined,
  disableAnimation: undefined,
  status: 'default',
  theme: {}
};

export default StatusIndicator;
